import React from 'react';
import { Box, Paper, Typography, Chip, Stack, IconButton, Tooltip } from '@mui/material';
import { TrendingUp, TrendingDown, ShowChart, Balance, Edit, Warning } from '@mui/icons-material';
import { WatchlistEntry } from '../../types';
import { formatCurrency } from '../../utils/formatters';

interface MobileWatchlistCardProps {
  entry: WatchlistEntry & {
    stopLoss?: number;
    targetPrice?: number;
    lastUpdated?: Date;
    priceWhenAdded?: number;
    companyName?: string;
  };
  hasActivePosition?: boolean;
  onViewChart?: (entry: WatchlistEntry) => void;
  onTrade?: (entry: WatchlistEntry, event: React.MouseEvent<HTMLElement>) => void;
  onEdit?: (entry: WatchlistEntry) => void;
}

const MobileWatchlistCard: React.FC<MobileWatchlistCardProps> = ({
  entry,
  hasActivePosition = false,
  onViewChart,
  onTrade,
  onEdit,
}) => {
  const priceChange = entry.priceChgPercent || 0;
  const isPositive = priceChange >= 0;

  const getDistanceColor = (distance: number) => {
    const absDistance = Math.abs(distance);
    if (absDistance <= 2) return 'success';
    if (absDistance <= 5) return 'warning';
    return 'default';
  };

  return (
    <Paper
      elevation={1}
      sx={{
        p: 1.5,
        mb: 1,
        borderRadius: 2,
        borderLeft: '4px solid',
        borderLeftColor: entry.distanceFromPivot !== undefined && entry.distanceFromPivot !== null
          ? (Math.abs(entry.distanceFromPivot) <= 2 ? 'success.main' :
             Math.abs(entry.distanceFromPivot) <= 5 ? 'warning.main' : 'grey.400')
          : 'warning.main',
      }}
    >
      {/* Top Row: Symbol, Price Change, Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="subtitle1" fontWeight="bold">
              {entry.symbol}
            </Typography>
            {hasActivePosition && (
              <Tooltip title="You own this stock" arrow>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    flexShrink: 0,
                  }}
                />
              </Tooltip>
            )}
          </Stack>
          <Typography variant="caption" color="textSecondary" noWrap sx={{ maxWidth: 120, display: 'block' }}>
            {entry.companyName || entry.symbol}
          </Typography>
        </Box>

        {/* Price & Change */}
        <Box sx={{ textAlign: 'right', mr: 1 }}>
          <Typography variant="body2" fontWeight="medium">
            {formatCurrency(entry.currentPrice)}
          </Typography>
          <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.25}>
            {isPositive ? (
              <TrendingUp sx={{ fontSize: 14 }} color="success" />
            ) : (
              <TrendingDown sx={{ fontSize: 14 }} color="error" />
            )}
            <Typography
              variant="caption"
              fontWeight="bold"
              color={isPositive ? 'success.main' : 'error.main'}
            >
              {isPositive ? '+' : ''}{priceChange.toFixed(2)}%
            </Typography>
          </Stack>
        </Box>

        {/* Action Buttons */}
        <Stack direction="row" spacing={0.25}>
          {onViewChart && (
            <IconButton
              size="small"
              onClick={() => onViewChart(entry)}
              sx={{ p: 0.5 }}
            >
              <ShowChart fontSize="small" />
            </IconButton>
          )}
          <Tooltip title="View on Finviz">
            <IconButton
              size="small"
              component="a"
              href={`https://finviz.com/quote.ashx?t=${entry.symbol}&p=d`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ p: 0.5 }}
            >
              <img
                src="https://finviz.com/favicon.ico"
                alt="Finviz"
                style={{ width: 16, height: 16 }}
              />
            </IconButton>
          </Tooltip>
          {onTrade && (
            <IconButton
              size="small"
              color="success"
              onClick={(e) => onTrade(entry, e)}
              sx={{ p: 0.5 }}
            >
              <Balance fontSize="small" />
            </IconButton>
          )}
          {onEdit && (
            <IconButton
              size="small"
              color="primary"
              onClick={() => onEdit(entry)}
              sx={{ p: 0.5 }}
            >
              <Edit fontSize="small" />
            </IconButton>
          )}
        </Stack>
      </Box>

      {/* Bottom Row: Key Stats */}
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {/* Distance from Pivot */}
        {entry.pivotPrice && entry.distanceFromPivot !== null && entry.distanceFromPivot !== undefined ? (
          <Chip
            label={`${entry.distanceFromPivot >= 0 ? '+' : ''}${entry.distanceFromPivot.toFixed(1)}%`}
            size="small"
            color={getDistanceColor(entry.distanceFromPivot)}
            icon={entry.distanceFromPivot >= 0 ? <TrendingUp /> : <TrendingDown />}
          />
        ) : (
          <Chip
            label="No pivot"
            size="small"
            icon={<Warning />}
            color="warning"
            variant="outlined"
          />
        )}

        {/* Volume % */}
        {entry.volumeChgVs50Day !== undefined && (
          <Chip
            label={`Vol: ${entry.volumeChgVs50Day > 0 ? '+' : ''}${entry.volumeChgVs50Day.toFixed(0)}%`}
            size="small"
            color={entry.volumeChgVs50Day > 50 ? 'success' : entry.volumeChgVs50Day > 0 ? 'default' : 'error'}
            variant="outlined"
          />
        )}

        {/* Base Pattern */}
        {entry.weeklyBaseType && (
          <Chip
            label={`${entry.weeklyBaseType}${entry.weeklyBaseStage ? ` S${entry.weeklyBaseStage}` : ''}`}
            size="small"
            variant="outlined"
          />
        )}
      </Box>
    </Paper>
  );
};

export default MobileWatchlistCard;
